import React, { lazy, Suspense } from 'react';
import { createBrowserRouter, Outlet } from 'react-router-dom';

import Root from 'routes/root';
import pages from 'pages';
import { AdminContextProvider } from 'contexts/AdminContext';
import { DashboardLayout } from 'layouts/DashboardLayout';
import { AuthGuard } from 'guards/AuthGuard';
import { DashboardGuard } from 'guards/DashboardGuard';
import { AdminGuard } from 'guards/AdminGuard';
import { Account } from 'modules/account/screens/Account';
import { StatusWrapper as Status } from 'routes/Status';
import { Layout } from 'shared/components/Layout/Layout';
import { LoadingLogo } from 'shared/components/Modal/LoadingLogo';
import OAuth2Authorize from 'modules/auth/screens/oauth2/OAuth2Authorize';
import { ExpiringSessionModal } from 'shared/components/ExpiringSessionModal/ExpiringSessionModal';
import { AutoPayRelay } from 'modules/status/components/AutoPayRelay/AutoPayRelay';
import ForceLogout from 'modules/auth/screens/ForceLogout/ForceLogout';
import { MagicLinkRelay } from 'modules/auth/screens/oauth2/MagicLinkRelay';
import { MyEverBrightQRRelay } from 'modules/status/components/Onboarding/MyEverBright/MyEverBrightQRRelay/MyEverBrightQRRelay';
import { FeatureFlagGuard } from 'guards/FeatureFlagGuard';
import { FlagKey } from 'api/types';
import { Dashboard } from './dashboard';
import { Support } from './support';
import { Admin } from './admin/admin';
import { Auth } from './auth';
import { DjangoAdmin } from './django-admin';

const ErrorFallback = lazy(() => import('shared/components/ErrorBoundary/ErrorFallback'));
const Chat = lazy(() => import('modules/chat/screens/Chat'));
const TransferRequestForm = lazy(
  () => import('modules/agreement-transfer/screens/TransferRequestForm/TransferRequestForm'),
);

export const router = createBrowserRouter([
  {
    path: '/',
    element: <Root />,
    errorElement: (
      <Suspense fallback={<LoadingLogo />}>
        <ErrorFallback />
      </Suspense>
    ),
    children: [
      {
        element: <DjangoAdmin />,
        path: 'django_admin',
      },
      {
        path: pages.MAGIC_LINK_AUTHORIZE,
        element: <MagicLinkRelay />,
      },
      {
        path: pages.MYEVERBRIGHT_QR_RELAY,
        element: <MyEverBrightQRRelay />,
      },
      {
        path: pages.OAUTH2_AUTHORIZE,
        element: <OAuth2Authorize />,
      },
      {
        path: pages.FORCE_LOGOUT,
        element: <ForceLogout />,
      },
      {
        element: (
          <>
            <Outlet />
            <ExpiringSessionModal />
          </>
        ),
        children: [
          {
            element: <AuthGuard needsSession redirectPath={pages.LOGIN} />,
            children: [
              {
                element: (
                  <AdminGuard isAdminDashboard={false} redirectPath={pages.ADMIN}>
                    <DashboardGuard />
                  </AdminGuard>
                ),
                children: Dashboard,
              },

              {
                element: (
                  <AdminGuard isAdminDashboard redirectPath={pages.DASHBOARD}>
                    <AdminContextProvider>
                      <Outlet />
                    </AdminContextProvider>
                  </AdminGuard>
                ),
                children: Admin,
              },
              {
                element: <DashboardLayout />,
                children: [
                  { path: pages.ACCOUNT, element: <Account /> },
                  { path: `${pages.AGREEMENTS}/:achAgreementId/ach`, element: <AutoPayRelay /> },
                  { path: pages.STATUS, element: <Status /> },
                  ...Support,
                ],
              },
            ],
          },
        ],
      },
      {
        path: pages.TRANSFER_REQUEST,
        element: (
          <Suspense fallback={<LoadingLogo />}>
            <FeatureFlagGuard flagKey={FlagKey.homeownerTransferSelfService}>
              <TransferRequestForm />
            </FeatureFlagGuard>
          </Suspense>
        ),
      },
      {
        element: <AuthGuard needsSession={false} redirectPath={`/${pages.DASHBOARD}`} />,
        children: Auth,
      },
      {
        path: pages.CHAT,
        element: (
          <Suspense fallback={<LoadingLogo />}>
            <Layout hasFooter>
              <Chat />
            </Layout>
          </Suspense>
        ),
      },
    ],
  },
]);

export default router;
